import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { useModalA11y } from "@/lib/useModalA11y";
import { formatRelative } from "@/features/devices/adapters";
import { listWorkspaceMachines } from "@/features/machines/api";
import type { MachineSummary } from "@/features/machines/projection";
import { IconLayers, IconPlus } from "@/lib/icons";
import { useWorkspaces } from "../useWorkspaces";

interface WorkflowRuntime {
  deviceCount: number;
  channelCount: number;
  lastSeenAt: string | null;
  status: "online" | "offline" | "neutral";
}

type RuntimeMap = Record<string, WorkflowRuntime>;

/**
 * Home `/workflows`. Lista todos os workflows locais (com seed virtual
 * `workspace-local` quando o storage está vazio) e oferece o botão de
 * criar novo workflow. Para cada workflow, consulta a API o conjunto
 * de gateways e canais já claimados naquele workspaceId.
 */
export function WorkspacesPage() {
  // Servidor quando ha identidade, `localStorage` so na bancada. A migracao
  // dos setores que ficaram em navegadores acontece dentro do hook.
  const {
    workspaces: workflows,
    criar,
    migrados,
    podeCriar,
    carregando: carregandoLista,
    erro: erroDaLista,
  } = useWorkspaces();
  const [runtime, setRuntime] = useState<RuntimeMap>({});
  const [searchParams, setSearchParams] = useSearchParams();
  // A URL e a fonte da verdade, como no modal de adicionar dispositivo: um
  // `useState` alimentado por efeito nao abre quando a pessoa ja esta nesta
  // pagina e clica em "Novo workflow" na barra lateral.
  const isCreateOpen = searchParams.get("novo") === "1";
  const setIsCreateOpen = useCallback(
    (aberto: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (aberto) next.set("novo", "1");
      else next.delete("novo");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createModalRef = useModalA11y({ open: isCreateOpen, onClose: () => setIsCreateOpen(false) });

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    async function loadRuntime() {
      const entries = await Promise.all(
        workflows.map(async (workflow): Promise<[string, WorkflowRuntime]> => {
          try {
            const machines = await listWorkspaceMachines(workflow.id, ctrl.signal);
            return [workflow.id, summarizeMachines(machines)];
          } catch {
            return [
              workflow.id,
              {
                deviceCount: 0,
                channelCount: 0,
                lastSeenAt: null,
                status: "offline",
              },
            ];
          }
        }),
      );
      if (cancelled) return;
      setRuntime(Object.fromEntries(entries));
    }

    if (workflows.length > 0) {
      void loadRuntime();
    }

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [workflows]);

  const totals = useMemo(
    () =>
      Object.values(runtime).reduce(
        (acc, item) => ({
          deviceCount: acc.deviceCount + item.deviceCount,
          channelCount: acc.channelCount + item.channelCount,
          onlineCount: acc.onlineCount + (item.status === "online" ? 1 : 0),
        }),
        { deviceCount: 0, channelCount: 0, onlineCount: 0 },
      ),
    [runtime],
  );

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) {
      setError("Informe um nome para o workflow.");
      return;
    }

    try {
      await criar(name, description);
    } catch (erro) {
      // O erro do servidor e util aqui: nome repetido volta como 409 com
      // texto proprio, e o operador precisa ler para escolher outro.
      setError(erro instanceof Error ? erro.message : "Falha ao criar o setor.");
      return;
    }
    setName("");
    setDescription("");
    setError(null);
    setIsCreateOpen(false);
  }

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Dashboard", to: "/workflows" }, { label: "Workflows" }]}
        title="Workflows"
        meta={
          <>
            <span className="item">
              Total <b>{workflows.length}</b>
            </span>
            <span className="item">
              Gateways <b>{totals.deviceCount}</b>
            </span>
            <span className="item">
              Sinais <b>{totals.channelCount}</b>
            </span>
            <span className="item">
              Online <b>{totals.onlineCount}</b>
            </span>
          </>
        }
      />
      <div className="content">
        {erroDaLista ? (
          <div className="state" role="alert">
            <h2 className="state__title">Nao foi possivel carregar os setores</h2>
            <p className="state__message">{erroDaLista}</p>
          </div>
        ) : null}

        {migrados > 0 ? (
          <div className="state" role="status">
            <h2 className="state__title">
              {migrados === 1
                ? "1 setor passou a existir no servidor"
                : `${migrados} setores passaram a existir no servidor`}
            </h2>
            <p className="state__message">
              Eles estavam salvos apenas neste navegador e por isso nao apareciam para mais
              ninguem — nem para os dispositivos. Agora pertencem a organizacao.
            </p>
          </div>
        ) : null}

        <div className="section-h">
          <h2>
            Linhas operacionais <span className="num">{workflows.length}</span>
          </h2>
          <div className="right">
            {podeCriar ? (
              <button
                type="button"
                className="btn primary sm"
                onClick={() => setIsCreateOpen(true)}
              >
                <IconPlus size={12} /> Criar workflow
              </button>
            ) : null}
          </div>
        </div>

        {carregandoLista && workflows.length === 0 ? (
          // Lista vazia por AINDA nao ter chegado nao e lista vazia. Enquanto
          // ela vinha do `localStorage` a distincao nao existia — o valor
          // estava la no primeiro render. Vindo do servidor, anunciar "nenhum
          // workflow criado" no meio do carregamento diz a quem tem setores
          // que eles sumiram.
          <div className="state">
            <h2 className="state__title">Carregando setores...</h2>
          </div>
        ) : workflows.length === 0 ? (
          <div className="state">
            <h2 className="state__title">Nenhum workflow criado</h2>
            <p className="state__message">
              {podeCriar
                ? "Crie o primeiro workflow para representar uma linha, setor ou célula de produção. Depois associe os gateways GHM dentro dele."
                : "Nenhum setor foi criado nesta organização ainda. Peça a um administrador para criar o primeiro."}
            </p>
            {/* Mesma condicao do botao do cabecalho: oferecer a um operador um
                botao que responde 403 e prometer o que nao se cumpre. */}
            {podeCriar ? (
              <div className="state__actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setIsCreateOpen(true)}
                >
                  <IconPlus size={12} /> Criar workflow
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {workflows.length > 0 ? (
          <div className="workflow-grid">
            {workflows.map((workflow, index) => {
              const stats = runtime[workflow.id] ?? {
                deviceCount: 0,
                channelCount: 0,
                lastSeenAt: null,
                status: "neutral",
              };
              const toneClass = stats.status === "online" ? "ok" : "muted";
              return (
                <Link
                  key={workflow.id}
                  to={`/workflows/${encodeURIComponent(workflow.id)}`}
                  className="w workflow-card"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="w-hd">
                    <div className="w-hd-l">
                      <div className="w-title">
                        {workflow.name}
                      </div>
                      <div className="w-sub">
                        {workflow.description ?? `Workflow local: ${workflow.id}`}
                      </div>
                    </div>
                    <div className="w-hd-r">
                      <span className="badge brand mono">WF{index + 1}</span>
                    </div>
                  </div>
                  <div className="w-body row">
                    <div className="state-r" style={{ gap: 18 }}>
                      <div className="state-txt">
                        <div className="state-val" style={{ fontSize: 22 }}>
                          {stats.deviceCount}
                          <span
                            className="u"
                            style={{ fontSize: 12, marginLeft: 4, color: "var(--ink-500)" }}
                          >
                            gateway(s)
                          </span>
                        </div>
                        <div className="state-meta">{stats.channelCount} sinal(is)</div>
                      </div>
                    </div>
                  </div>
                  <div className="w-ft">
                    <span className="ts">
                      <span className="clk" />
                      último sinal {formatRelative(stats.lastSeenAt)}
                    </span>
                    <span className={`badge ${toneClass}`}>
                      <span className={stats.status === "online" ? "led-anim" : "dot"} />
                      {stats.status === "online" ? "ONLINE" : "AGUARDANDO"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>

      {isCreateOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsCreateOpen(false);
          }}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-workflow-title"
            ref={createModalRef}
            tabIndex={-1}
          >
            <header className="modal__header">
              <div>
                <p className="page__eyebrow">Workflow</p>
                <h2 id="create-workflow-title">Criar novo workflow</h2>
              </div>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setIsCreateOpen(false)}
              >
                Fechar
              </button>
            </header>

            <form className="modal__body" onSubmit={handleCreate}>
              <label className="field">
                <span className="field__label">Nome</span>
                <input
                  className="input field__control"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Linha de Extrusão 01"
                  autoFocus
                />
              </label>

              <label className="field">
                <span className="field__label">Descrição</span>
                <input
                  className="input field__control"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Setor, célula ou linha monitorada"
                />
              </label>

              {error ? (
                <div className="inline-warning" role="status">
                  {error}
                </div>
              ) : null}

              <div className="modal__actions">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setIsCreateOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn primary">
                  <IconLayers size={12} /> Criar workflow
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

function summarizeMachines(machines: MachineSummary[]): WorkflowRuntime {
  return machines.reduce<WorkflowRuntime>(
    (acc, machine) => ({
      deviceCount: acc.deviceCount + machine.deviceCount,
      channelCount: acc.channelCount + machine.channelCount,
      lastSeenAt: latestDate(acc.lastSeenAt, machine.lastSeenAt),
      status:
        acc.status === "online" || machine.status === "online"
          ? "online"
          : acc.status === "neutral" || machine.status === "neutral"
            ? "neutral"
            : "offline",
    }),
    { deviceCount: 0, channelCount: 0, lastSeenAt: null, status: "offline" },
  );
}

function latestDate(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return left.localeCompare(right) > 0 ? left : right;
}
